import React from "react";
import { MOBILE_TOOL_PANEL_PIN_IDS } from "../../constants/mobileToolPanelPins";
import {
  useMobilePinnedToolPanelActions,
  useMobilePinnedToolPanelId,
} from "../../stores/mobilePinnedToolPanelStore";
import {
  MOBILE_PINNED_TOOL_PANEL_TAB_META,
  isMobilePinnedToolPanelIdInApp,
} from "../../utils/mobilePinnedToolPanelMeta";

export function usePinnedToolPanelTabConfig({
  isMobilePanelViewport,
  showDevTools,
  showTrainingIkTools,
  sceneGraphOpen,
  setSceneGraphOpenFromMenu,
  closeSceneGraph,
  sceneInventoryOpen,
  setSceneInventoryOpenFromMenu,
  closeSceneInventory,
  modelCheckOpen,
  setModelCheckOpenFromMenu,
  closeModelCheckPanel,
  massPropertiesOpen,
  setMassPropertiesOpenFromMenu,
  closeMassPropertiesPanel,
  filePanelOpen,
  setFilePanelOpenFromMenu,
  closeFilePanel,
  animationPanelOpen,
  setAnimationPanelOpenFromMenu,
  closeAnimationPanel,
  materialEditorPanelOpen,
  setMaterialEditorPanelOpenFromMenu,
  closeMaterialEditorPanel,
  lightsPanelOpen,
  setLightsPanelOpenFromMenu,
  closeLightsPanel,
  ikTargetPanelOpen,
  setIkTargetPanelOpenFromMenu,
  closeIkTargetPanel,
  runtimeControlsOpen,
  openRuntimeControlsPanel,
  closeRuntimeControlsPanel,
  physicsPanelOpen,
  openPhysicsPanel,
  closePhysicsPanel,
  actuatorControlsOpen,
  setActuatorControlsOpenFromMenu,
  closeActuatorControlsPanel,
  warmstartDatasetOpen,
  openWarmstartDatasetPanel,
  closeWarmstartDatasetPanel,
  behaviorEditorOpen,
  setBehaviorEditorOpenFromMenu,
  closeBehaviorEditorPanel,
  trainingPipelineOpen,
  setTrainingPipelineOpenFromMenu,
  closeTrainingPipelinePanel,
  octoTrainingOpen,
  setOctoTrainingOpenFromMenu,
  closeOctoTrainingPanel,
  trainingMetricsOpen,
  setTrainingMetricsOpenFromMenu,
  closeTrainingMetricsPanel,
  policyRolloutOpen,
  openPolicyRolloutPanel,
  closePolicyRolloutPanel,
  circleDetectionOpen,
  setCircleDetectionOpenFromMenu,
  closeCircleDetectionPanel,
  historyPanelOpen,
  setHistoryPanelOpenFromMenu,
  closeHistoryPanel,
  runtimeDebugOpen,
  setRuntimeDebugOpenFromMenu,
  closeRuntimeDebugPanel,
  measurementsOpen,
  setMeasurementsOpenFromMenu,
  closeMeasurementsPanel,
  meshEditorOpen,
  setMeshEditorOpenFromMenu,
  closeMeshEditorPanel,
  xmlSourcePanelOpen,
  setXmlSourcePanelOpenFromMenu,
  closeXmlSourcePanel,
  terrainPanelOpen,
  setTerrainPanelOpenFromMenu,
  closeTerrainPanel,
  objectLibraryOpen,
  setObjectLibraryOpenFromMenu,
  closeObjectLibraryPanel,
  cameraProbeOpen,
  setCameraProbeOpenFromMenu,
  closeCameraProbePanel,
  sensorsOpen,
  setSensorsOpenFromMenu,
  closeSensorsPanel,
}) {
  const pinnedPanelId = useMobilePinnedToolPanelId();
  const { setPinnedPanelId } = useMobilePinnedToolPanelActions();

  React.useEffect(() => {
    if (!pinnedPanelId) return;
    if (!isMobilePinnedToolPanelIdInApp(pinnedPanelId, { showDevTools, showTrainingIkTools })) {
      setPinnedPanelId(null);
    }
  }, [pinnedPanelId, showDevTools, showTrainingIkTools, setPinnedPanelId]);

  const pinnedToolPanelTabConfig = React.useMemo(() => {
    if (!pinnedPanelId || !isMobilePanelViewport) return null;
    const pinMeta = MOBILE_PINNED_TOOL_PANEL_TAB_META[pinnedPanelId];
    if (!pinMeta?.Icon) return null;

    const make = (isOpen, openExclusive, close) => ({
      Icon: pinMeta.Icon,
      label: pinMeta.label,
      isOpen,
      onToggle: () => {
        if (isOpen) close();
        else openExclusive();
      },
      onSwipeOpen: openExclusive,
    });

    const I = MOBILE_TOOL_PANEL_PIN_IDS;
    switch (pinnedPanelId) {
      case I.sceneGraph:
        return make(sceneGraphOpen, () => setSceneGraphOpenFromMenu(true), closeSceneGraph);
      case I.sceneInventory:
        return make(
          sceneInventoryOpen,
          () => setSceneInventoryOpenFromMenu(true),
          closeSceneInventory
        );
      case I.modelCheck:
        return make(modelCheckOpen, () => setModelCheckOpenFromMenu(true), closeModelCheckPanel);
      case I.massProperties:
        return make(
          massPropertiesOpen,
          () => setMassPropertiesOpenFromMenu(true),
          closeMassPropertiesPanel
        );
      case I.fileUpload:
        return make(filePanelOpen, () => setFilePanelOpenFromMenu(true), closeFilePanel);
      case I.poseEditor:
        return make(
          animationPanelOpen,
          () => setAnimationPanelOpenFromMenu(true),
          closeAnimationPanel
        );
      case I.materialEditor:
        return make(
          materialEditorPanelOpen,
          () => setMaterialEditorPanelOpenFromMenu(true),
          closeMaterialEditorPanel
        );
      case I.lights:
        return make(lightsPanelOpen, () => setLightsPanelOpenFromMenu(true), closeLightsPanel);
      case I.ikTarget:
        return make(
          ikTargetPanelOpen,
          () => setIkTargetPanelOpenFromMenu(true),
          closeIkTargetPanel
        );
      case I.runtimeControls:
        return make(runtimeControlsOpen, openRuntimeControlsPanel, closeRuntimeControlsPanel);
      case I.physics:
        return make(physicsPanelOpen, openPhysicsPanel, closePhysicsPanel);
      case I.actuators:
        return make(
          actuatorControlsOpen,
          () => setActuatorControlsOpenFromMenu(true),
          closeActuatorControlsPanel
        );
      case I.warmstart:
        return make(warmstartDatasetOpen, openWarmstartDatasetPanel, closeWarmstartDatasetPanel);
      case I.behaviorEditor:
        return make(
          behaviorEditorOpen,
          () => setBehaviorEditorOpenFromMenu(true),
          closeBehaviorEditorPanel
        );
      case I.trainingPipeline:
        return make(
          trainingPipelineOpen,
          () => setTrainingPipelineOpenFromMenu(true),
          closeTrainingPipelinePanel
        );
      case I.octoTraining:
        return make(
          octoTrainingOpen,
          () => setOctoTrainingOpenFromMenu(true),
          closeOctoTrainingPanel
        );
      case I.trainingMetrics:
        return make(
          trainingMetricsOpen,
          () => setTrainingMetricsOpenFromMenu(true),
          closeTrainingMetricsPanel
        );
      case I.policyRollout:
        return make(policyRolloutOpen, () => openPolicyRolloutPanel(null), closePolicyRolloutPanel);
      case I.circleDetection:
        return make(
          circleDetectionOpen,
          () => setCircleDetectionOpenFromMenu(true),
          closeCircleDetectionPanel
        );
      case I.history:
        return make(historyPanelOpen, () => setHistoryPanelOpenFromMenu(true), closeHistoryPanel);
      case I.assetStats:
        return make(
          runtimeDebugOpen,
          () => setRuntimeDebugOpenFromMenu(true),
          closeRuntimeDebugPanel
        );
      case I.measurements:
        return make(
          measurementsOpen,
          () => setMeasurementsOpenFromMenu(true),
          closeMeasurementsPanel
        );
      case I.meshEditor:
        return make(meshEditorOpen, () => setMeshEditorOpenFromMenu(true), closeMeshEditorPanel);
      case I.xmlSource:
        return make(
          xmlSourcePanelOpen,
          () => setXmlSourcePanelOpenFromMenu(true),
          closeXmlSourcePanel
        );
      case I.terrain:
        return make(terrainPanelOpen, () => setTerrainPanelOpenFromMenu(true), closeTerrainPanel);
      case I.objectLibrary:
        return make(
          objectLibraryOpen,
          () => setObjectLibraryOpenFromMenu(true),
          closeObjectLibraryPanel
        );
      case I.cameraProbe:
        return make(cameraProbeOpen, () => setCameraProbeOpenFromMenu(true), closeCameraProbePanel);
      case I.sensors:
        return make(sensorsOpen, () => setSensorsOpenFromMenu(true), closeSensorsPanel);
      default:
        return null;
    }
  }, [
    pinnedPanelId,
    isMobilePanelViewport,
    sceneGraphOpen,
    setSceneGraphOpenFromMenu,
    closeSceneGraph,
    sceneInventoryOpen,
    setSceneInventoryOpenFromMenu,
    closeSceneInventory,
    modelCheckOpen,
    setModelCheckOpenFromMenu,
    closeModelCheckPanel,
    massPropertiesOpen,
    setMassPropertiesOpenFromMenu,
    closeMassPropertiesPanel,
    filePanelOpen,
    setFilePanelOpenFromMenu,
    closeFilePanel,
    animationPanelOpen,
    setAnimationPanelOpenFromMenu,
    closeAnimationPanel,
    materialEditorPanelOpen,
    setMaterialEditorPanelOpenFromMenu,
    closeMaterialEditorPanel,
    lightsPanelOpen,
    setLightsPanelOpenFromMenu,
    closeLightsPanel,
    ikTargetPanelOpen,
    setIkTargetPanelOpenFromMenu,
    closeIkTargetPanel,
    runtimeControlsOpen,
    openRuntimeControlsPanel,
    closeRuntimeControlsPanel,
    physicsPanelOpen,
    openPhysicsPanel,
    closePhysicsPanel,
    actuatorControlsOpen,
    setActuatorControlsOpenFromMenu,
    closeActuatorControlsPanel,
    warmstartDatasetOpen,
    openWarmstartDatasetPanel,
    closeWarmstartDatasetPanel,
    behaviorEditorOpen,
    setBehaviorEditorOpenFromMenu,
    closeBehaviorEditorPanel,
    trainingPipelineOpen,
    setTrainingPipelineOpenFromMenu,
    closeTrainingPipelinePanel,
    octoTrainingOpen,
    setOctoTrainingOpenFromMenu,
    closeOctoTrainingPanel,
    trainingMetricsOpen,
    setTrainingMetricsOpenFromMenu,
    closeTrainingMetricsPanel,
    policyRolloutOpen,
    openPolicyRolloutPanel,
    closePolicyRolloutPanel,
    circleDetectionOpen,
    setCircleDetectionOpenFromMenu,
    closeCircleDetectionPanel,
    historyPanelOpen,
    setHistoryPanelOpenFromMenu,
    closeHistoryPanel,
    runtimeDebugOpen,
    setRuntimeDebugOpenFromMenu,
    closeRuntimeDebugPanel,
    measurementsOpen,
    setMeasurementsOpenFromMenu,
    closeMeasurementsPanel,
    meshEditorOpen,
    setMeshEditorOpenFromMenu,
    closeMeshEditorPanel,
    xmlSourcePanelOpen,
    setXmlSourcePanelOpenFromMenu,
    closeXmlSourcePanel,
    terrainPanelOpen,
    setTerrainPanelOpenFromMenu,
    closeTerrainPanel,
    objectLibraryOpen,
    setObjectLibraryOpenFromMenu,
    closeObjectLibraryPanel,
    cameraProbeOpen,
    setCameraProbeOpenFromMenu,
    closeCameraProbePanel,
    sensorsOpen,
    setSensorsOpenFromMenu,
    closeSensorsPanel,
  ]);

  return { pinnedPanelId, pinnedToolPanelTabConfig };
}
